import { useEffect, useRef } from 'react';
import jwtDecode from 'jwt-decode';
import { LOCATIONS, ROLES, USER_CONTEXT, setJanusToken } from '../../constants';
import { fetchJanusToken, getAvailableAssignedRoles } from '../../util/APIUtils';
import { UserStorage } from '../../util/UserStorage';
import { HU_CACHE_KEY, LOCATION_CACHE_KEY, LOCATION_NAME_REGEX, LOCATION_TYPE } from '../services/constants';

export const USER_MANAGEMENT_PATH = '/user-management';

const routeSlug = (name) => (name || '').replace(LOCATION_NAME_REGEX, '');

/**
 * Location Management nav and home card. Same gate in the legacy drawer, the new side nav, and home.
 * Route permission is applied by the caller; this is only the flag and the blocked roles.
 */
export const canShowLocationManagement = ({ splitEnabled, isFacilityStaff, isAttorney, isMessagingOnly }) =>
  Boolean(splitEnabled) && !isFacilityStaff && !isAttorney && !isMessagingOnly;

export const buildActiveLocation = (selectedLocation, selectedHousingUnit, locations = LOCATIONS) => {
  const activeRecord = selectedHousingUnit || selectedLocation;
  if (!activeRecord || activeRecord.locationId == null) {
    return null;
  }

  const isHousingUnit = Boolean(selectedHousingUnit);
  const parentLocation = isHousingUnit
    ? locations.find((item) => item.locationId === selectedHousingUnit.parentLocationId)
    : null;

  return {
    active: activeRecord.locationId,
    collection: locations,
    locationName: activeRecord.locationName,
    type: isHousingUnit ? 'hu' : 'facility',
    parentLocationId: isHousingUnit ? selectedHousingUnit.parentLocationId || '' : '',
    parentLocationName: isHousingUnit ? parentLocation?.locationName || '' : '',
  };
};

export const defaultManageLocation = (locations, cachedLocation) => {
  if (cachedLocation) {
    return cachedLocation;
  }
  const list = locations || [];
  return (
    list.find((loc) => loc.locationTypeId === 3 && loc.active === true) ||
    list.find((loc) => loc.active === true) ||
    null
  );
};

const findLocationBySlug = (locations, slug) => (locations || []).find((loc) => routeSlug(loc.locationName) === slug);

const selectFromManageUrl = ({ locationName, housingUnitName, locations }) => {
  if (!locationName) {
    return null;
  }
  const facility = findLocationBySlug(locations, locationName);
  if (!facility) {
    return null;
  }
  if (!housingUnitName) {
    return { action: 'select', facility, housingUnit: null };
  }
  const housingUnit = (locations || []).find(
    (loc) => routeSlug(loc.locationName) === housingUnitName && loc.parentLocationId === facility.locationId
  );
  if (!housingUnit) {
    return null;
  }
  return { action: 'select', facility, housingUnit };
};

const managePathForSelection = (selectedLocation, selectedHousingUnit) => {
  if (!selectedLocation) {
    return null;
  }
  const facilitySlug = routeSlug(selectedLocation.locationName);
  return selectedHousingUnit
    ? `${USER_MANAGEMENT_PATH}/${facilitySlug}/${routeSlug(selectedHousingUnit.locationName)}`
    : `${USER_MANAGEMENT_PATH}/${facilitySlug}`;
};

const redirectBareManage = ({
  pathname,
  locationName,
  selectedLocation,
  selectedHousingUnit,
  locations,
  cachedLocation,
}) => {
  if (pathname !== USER_MANAGEMENT_PATH || locationName) {
    return null;
  }
  if (selectedHousingUnit && selectedLocation) {
    return { action: 'redirect', to: managePathForSelection(selectedLocation, selectedHousingUnit) };
  }
  const target = selectedLocation || defaultManageLocation(locations, cachedLocation);
  if (!target) {
    return { action: 'clear-breadcrumbs' };
  }
  return {
    action: 'redirect',
    to: managePathForSelection(target, null),
    selectFacility: selectedLocation ? null : target,
  };
};

const replaceManagePathFromSelection = ({ pathname, selectedLocation, selectedHousingUnit }) => {
  if (!pathname || pathname === USER_MANAGEMENT_PATH || !selectedLocation) {
    return null;
  }
  const expected = managePathForSelection(selectedLocation, selectedHousingUnit);
  if (pathname === expected) {
    return { action: 'none' };
  }
  return { action: 'replace', to: expected };
};

/**
 * Same decision tree as Management.handleRouteChange. Pure so the flag-on /user-management page can
 * select a deep link and redirect bare /user-management without editing management.js.
 * `urlApplied` means Redux already matched this pathname, so a later selection change (header
 * picker, cache restore) rewrites the url instead of being reverted to it.
 */
export const resolveManageLocationRoute = (input) =>
  (!input.urlApplied && selectFromManageUrl(input)) ||
  redirectBareManage(input) ||
  replaceManagePathFromSelection(input) || { action: 'none' };

const readCachedManageLocation = () => {
  try {
    const cached = new UserStorage(USER_CONTEXT.personId).getItem(LOCATION_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error getting default location:', error);
    return null;
  }
};

/**
 * Same keys the header picker writes. URL-driven select only updates Redux, so nav
 * (`getActiveLocationId`) and the next load would keep the previous facility without this.
 */
export const persistManageLocationCache = (facility, housingUnit) => {
  if (!facility) {
    return;
  }
  try {
    const userStorage = new UserStorage(USER_CONTEXT.personId);
    userStorage.removeItem(LOCATION_CACHE_KEY);
    userStorage.removeItem(HU_CACHE_KEY);
    userStorage.removeItem(LOCATION_TYPE.KEY);
    userStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(facility));
    if (housingUnit) {
      userStorage.setItem(HU_CACHE_KEY, JSON.stringify(housingUnit));
      userStorage.setItem(LOCATION_TYPE.KEY, LOCATION_TYPE.HOUSING_UNIT);
    } else {
      userStorage.setItem(LOCATION_TYPE.KEY, LOCATION_TYPE.FACILITY);
    }
  } catch (error) {
    console.error('Error saving location to cache:', error);
  }
};

export const useSyncManageRoute = ({
  match,
  location,
  history,
  selectedLocation,
  selectedHousingUnit,
  selectLocation,
  selectHousingUnit,
  setGlobal,
  locations = LOCATIONS,
}) => {
  const pathname = location?.pathname;
  const locationName = match?.params?.location;
  const housingUnitName = match?.params?.housingUnit;
  const appliedPathRef = useRef(null);
  const decision = resolveManageLocationRoute({
    pathname,
    locationName,
    housingUnitName,
    selectedLocation,
    selectedHousingUnit,
    locations,
    cachedLocation: pathname ? readCachedManageLocation() : null,
    urlApplied: Boolean(pathname) && appliedPathRef.current === pathname,
  });
  const decisionKey = [
    decision.action,
    decision.to || '',
    decision.facility?.locationId ?? '',
    decision.housingUnit?.locationId ?? '',
    decision.selectFacility?.locationId ?? '',
  ].join('|');

  useEffect(() => {
    if (decision.action === 'select') {
      persistManageLocationCache(decision.facility, decision.housingUnit);
      if (selectLocation && selectedLocation?.locationId !== decision.facility.locationId) {
        selectLocation(decision.facility);
      }
      const nextHousingId = decision.housingUnit?.locationId ?? null;
      if (selectHousingUnit && (selectedHousingUnit?.locationId ?? null) !== nextHousingId) {
        selectHousingUnit(decision.housingUnit);
      }
      return;
    }
    if (decision.action === 'replace') {
      history?.replace(decision.to);
      return;
    }
    if (decision.action === 'redirect' && decision.selectFacility) {
      persistManageLocationCache(decision.selectFacility, null);
      selectLocation?.(decision.selectFacility);
      return;
    }
    if (decision.action === 'clear-breadcrumbs') {
      setGlobal?.({ hiddenBreadcrumbItems: [] });
    }
    // decisionKey captures the fields this effect acts on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisionKey]);

  useEffect(() => {
    if (pathname && pathname === managePathForSelection(selectedLocation, selectedHousingUnit)) {
      appliedPathRef.current = pathname;
    }
  });

  return decision.action === 'redirect' ? decision.to : null;
};

const requestIsStale = (isStale) => typeof isStale === 'function' && isStale();

const resolveIsSystemAdmin = async (locationId, rolesTypes, jwtRoleIds, isStale) => {
  if (rolesTypes.includes(ROLES.SYSTEM_ADMIN)) {
    return true;
  }
  if (jwtRoleIds.length === 0) {
    return false;
  }
  const assignable = await getAvailableAssignedRoles(locationId);
  if (requestIsStale(isStale)) {
    return null;
  }
  const nameFrom = (role) => ((role && (role.displayName || role.roleName || role.roleDisplayName)) || '').trim();
  const systemAdminRow = (assignable || []).find((role) => nameFrom(role) === ROLES.SYSTEM_ADMIN);
  if (systemAdminRow == null || systemAdminRow.roleId == null) {
    return false;
  }
  const systemAdminRoleId = Number(systemAdminRow.roleId);
  return jwtRoleIds.some((id) => id === systemAdminRoleId);
};

const applyCbacLocationAccess = async (locationId, setGlobal, isStale) => {
  const janusToken = await fetchJanusToken(locationId);
  if (requestIsStale(isStale)) {
    return;
  }
  setJanusToken((janusToken && janusToken.token) || '');
  if (!janusToken || !janusToken.token) {
    return;
  }

  const decoded = jwtDecode(janusToken.token);
  const rolesTypes = Array.isArray(decoded.rolesTypes) ? decoded.rolesTypes : [];
  const jwtRoleIds = Array.isArray(decoded.roles) ? decoded.roles.map((id) => Number(id)) : [];
  const isSystemAdmin = await resolveIsSystemAdmin(locationId, rolesTypes, jwtRoleIds, isStale);
  if (isSystemAdmin == null) {
    return;
  }

  await setGlobal({
    isSuperAdmin: rolesTypes.includes(ROLES.SUPER_ADMIN),
    isSystemAdmin,
    isAttorney: rolesTypes.includes(ROLES.ATTORNEY),
    isTeacher: rolesTypes.includes(ROLES.TEACHER),
    isFacilityAdmin: rolesTypes.includes(ROLES.STAFF),
    isMessagingOnly: rolesTypes.includes(ROLES.MESSAGING_ONLY),
    selectedLocation: locationId,
  });
};

/**
 * Rewrites CBAC role flags from the selected location's Janus token.
 * Mirrors Management.updateGlobalStates. Tab checks must not pass global.permissionIds;
 * isPermitted then reads this token, the same way PermissionControl does.
 */
export const applySelectedLocationAccess = async (locationId, { cbacConfigFlag, setGlobal, isStale } = {}) => {
  if (locationId == null || typeof setGlobal !== 'function') {
    return;
  }

  if (!cbacConfigFlag) {
    await setGlobal({ selectedLocation: locationId });
    return;
  }

  await applyCbacLocationAccess(locationId, setGlobal, isStale);
};

export const useApplySelectedLocationAccess = (locationId, cbacConfigFlag, setGlobal) => {
  useEffect(() => {
    let cancelled = false;
    applySelectedLocationAccess(locationId, {
      cbacConfigFlag,
      isStale: () => cancelled,
      setGlobal: (patch) => {
        if (cancelled || typeof setGlobal !== 'function') {
          return undefined;
        }
        return setGlobal(patch);
      },
    }).catch((error) => {
      console.error('Error applying location access:', error);
    });
    return () => {
      cancelled = true;
    };
  }, [locationId, cbacConfigFlag, setGlobal]);
};
